import {
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import { z } from 'zod';

import type { CouponResponse } from '@/components/features/checkout/coupon';
import { pluralizeCredits } from '@/helpers/credits';
import { buildMinSpotsNotice } from '@/helpers/min-spots';
import { joinWithAnd } from '@/helpers/public-terms';
import { getOccurrenceId, instantOf, isLiveItem } from '@/helpers/order';
import { isSinglePayment } from '@/helpers/subscription';
import { toAcceptedTerms } from '@/helpers/terms';
import { GUEST_FIELD_REFUSAL_CODES } from '@/lib/constants';
import type {
  FactTile,
  GuestOf,
  GuestPassAdd,
  ReceiptGuest,
} from '@/lib/types';
import {
  formatInterval,
  formatIntervalForTerms,
  toDialCode,
} from '@/lib/utils';
import type { GuestInput } from '@/services/order';
import type { ResolvedTerms } from '@/services/terms';
import type { Subscription } from '@/state/subscription-groups';

export type CodeGuestPasses = { total: number; left: number };

export type GuestPassPlanKind = 'pack' | 'membership';

const passesText = (count: number) =>
  count === 1 ? '1 guest pass' : `${count} guest passes`;

const hasPasses = (count: number | null | undefined): count is number =>
  count != null && count >= 1;

export const packGuestPassLine = (
  count: number | null | undefined
): string | null => (hasPasses(count) ? passesText(count) : null);

type GuestPassPlan = Pick<
  Subscription,
  'guest_passes' | 'number_of_billing_cycles'
> & {
  charge_every_count?: number | null;
  charge_every_interval: string;
};

// "every month", "every 2 weeks"; null when the passes come once, as with a
// pack.
const passCycle = (plan: GuestPassPlan): string | null =>
  isSinglePayment(plan)
    ? null
    : formatIntervalForTerms(
        plan.charge_every_count || 1,
        plan.charge_every_interval
      );

export const membershipGuestPassLine = (plan: GuestPassPlan): string | null => {
  const count = plan.guest_passes;
  if (!hasPasses(count)) return null;
  if (isSinglePayment(plan)) return passesText(count);
  return `${passesText(count)}${formatInterval(
    plan.charge_every_count || 1,
    plan.charge_every_interval
  )}`;
};

// An admin can add passes above what the code was sold with, and "3 of 2"
// reads as a bug.
export const guestPassesLeftText = (
  passes: CodeGuestPasses | null | undefined
): string | null => {
  if (!passes) return null;
  return passes.left > passes.total
    ? `${passes.left} left`
    : `${passes.left} of ${passes.total} left`;
};

const TOOLTIP_BASE: Record<GuestPassPlanKind, string> = {
  pack: 'Bring a friend to a class you book with this pack.',
  membership: 'Bring a friend to a class you book with this membership.',
};

export type GuestPassRules = { newGuestsOnly?: boolean; studioName?: string };

const guestPassRuleLines = (
  kind: GuestPassPlanKind,
  count: number | null,
  cycle: string | null,
  { newGuestsOnly = false, studioName }: GuestPassRules
): string[] => {
  const lines = [
    TOOLTIP_BASE[kind],
    'Add them when you book, or later from your booking. Each guest uses one pass.',
  ];
  if (cycle) {
    lines.push(
      `You get ${count === 1 ? 'a new pass' : 'new passes'} ${cycle}. Unused passes don't roll over.`
    );
  }
  if (newGuestsOnly) {
    lines.push(`Your guest must be new to ${studioName || 'the studio'}.`);
  }
  return lines;
};

export const guestPassTooltip = (
  kind: GuestPassPlanKind,
  { singlePayment = false }: { singlePayment?: boolean } = {}
): string =>
  guestPassRuleLines(
    kind,
    null,
    kind === 'membership' && !singlePayment ? 'each billing cycle' : null,
    {}
  ).join(' ');

const passesWord = (count: number) =>
  count === 1 ? 'One guest pass' : `${count} guest passes`;

const guestPassTile = (
  kind: GuestPassPlanKind,
  count: number,
  cycle: string | null,
  rules: GuestPassRules
): FactTile => ({
  key: 'guest_passes',
  illustration: 'guest-pass-3d',
  // A membership's count leads like "Renews", so its cadence is the value.
  ...(cycle
    ? { lead: `${passesWord(count)} `, value: cycle }
    : { lead: '', value: passesWord(count), tail: ' included' }),
  info: {
    label: 'About guest passes',
    lines: guestPassRuleLines(kind, count, cycle, rules),
  },
});

export const packGuestPassTile = (
  count: number | null | undefined,
  rules: GuestPassRules
): FactTile | false =>
  hasPasses(count) && guestPassTile('pack', count, null, rules);

export const membershipGuestPassTile = (
  plan: GuestPassPlan,
  rules: GuestPassRules
): FactTile | false =>
  hasPasses(plan.guest_passes) &&
  guestPassTile('membership', plan.guest_passes, passCycle(plan), rules);

// Mirrors the backend's MAX_GUESTS_PER_CLASS_TIME (services/guest-booking-rules.ts).
const MAX_GUESTS_PER_BOOKING = 10;

export type CheckoutGuestPasses = { left: number; new_guests_only: boolean };

// The validate reply is the only gate: it carries guest_passes only for the
// signed-in owner of a pack or membership code with passes left.
export const checkoutGuestPasses = (
  coupon: CouponResponse | null | undefined
): CheckoutGuestPasses | null =>
  coupon?.valid && coupon.type === 'CUSTOM' && coupon.guest_passes
    ? coupon.guest_passes
    : null;

// Null when the applied code is unlimited or no pack or membership code.
export const appliedCodeCredits = (
  coupon: CouponResponse | null | undefined
): number | null =>
  coupon?.valid && coupon.type === 'CUSTOM'
    ? (coupon.class_pack_code?.classes_left ??
      coupon.subscription_code?.classes_left ??
      null)
    : null;

export const maxGuestsAtCheckout = ({
  left,
  availableSpots,
  quantity,
}: {
  left: number;
  availableSpots: number | null;
  quantity: number;
}): number =>
  Math.max(
    0,
    Math.min(
      left,
      availableSpots === null
        ? MAX_GUESTS_PER_BOOKING
        : availableSpots - quantity,
      MAX_GUESTS_PER_BOOKING
    )
  );

// Counts down as guest cards open, so the host sees what each one costs, and
// says nothing of the count once every pass is in use.
export const bringGuestHint = (
  passes: CheckoutGuestPasses,
  studioName?: string,
  used = 0,
  nudge?: string | null
): string | undefined => {
  const count = Math.max(passes.left - used, 0);
  const hint = [
    count > 0 && `${passesText(count)} left`,
    passes.new_guests_only &&
      `For people new to ${studioName || 'the studio'}`,
  ]
    .filter(Boolean)
    .join(' · ');
  return [hint, nudge].filter(Boolean).join('. ') || undefined;
};

// Hand-copy of the backend's class minimum at checkout (order.ts checks
// quantity plus guests): the guests count toward it, so each one lowers the
// host's own spots by one, never below 1.
export const ownSpotsFloor = (minSpots: number, guests: number): number =>
  Math.max(1, minSpots - guests);

// Refusals that only say the code can't carry this many of the host's own
// spots. At one spot it may, with guests filling the class minimum.
export const GUEST_FILLABLE_REFUSALS: readonly string[] = [
  'PACK_QUANTITY_EXCEEDS',
  'SUB_QUANTITY_EXCEEDS',
  'ONE_SPOT_PER_CLASS_LIMIT',
];

// A one-seat code books one own spot whatever it holds. A code holding the
// class minimum books it without guests: fewer own spots is the way in.
export const codeCoversMinimum = (
  errorCode: string,
  classesLeft: number | null | undefined,
  minSpots: number
): boolean =>
  (errorCode === 'ONE_SPOT_PER_CLASS_LIMIT' ? 1 : (classesLeft ?? 0)) >=
  minSpots;

export const guestsToFillMinimum = (
  minSpots: number,
  ownSpots: number
): number => Math.max(1, minSpots - ownSpots);

export const minSpotsGuestNudge = (
  minSpots: number,
  ownSpots: number
): string => {
  const needed = guestsToFillMinimum(minSpots, ownSpots);
  return `${buildMinSpotsNotice(minSpots)} Bring ${needed === 1 ? 'a guest' : `${needed} guests`} to book ${ownSpots === 1 ? '1 spot' : `${ownSpots} spots`} yourself.`;
};

const guestsText = (count: number) =>
  count === 1 ? '1 guest' : `${count} guests`;

const spotsText = (count: number) =>
  count === 1 ? '1 spot' : `${count} spots`;

export const bookingSizeText = (
  ownSpots: number,
  guests: number
): string | null => {
  if (guests < 1) return ownSpots > 1 ? spotsText(ownSpots) : null;
  return `${spotsText(ownSpots)} + ${guestsText(guests)}`;
};

export const addedGuestPassesText = (passes: number): string | null =>
  passes > 0 ? `+ ${passesText(passes)}` : null;

export const withGuestPasses = (label: string, passes: number): string => {
  const added = addedGuestPassesText(passes);
  return added ? `${label} ${added}` : label;
};

export const guestsTakeSpotsNotice = (
  guests: number,
  availableSpots: number
): string =>
  `Your ${guests === 1 ? 'guest takes' : 'guests take'} ${guests} of the ${availableSpots} spots left`;

// Empty cards lose the host nothing, so their going is not news.
export const hasGuestInput = (guests: GuestFormValue[]): boolean =>
  guests.some((guest) =>
    [guest.first_name, guest.last_name, guest.email, guest.phone].some(
      (value) => value.trim() !== ''
    )
  );

const guestsNotAdded = (count: number): string =>
  count === 1 ? "Your guest wasn't added." : "Your guests weren't added.";

export const guestsDroppedMessage = (count: number): string =>
  `You have no guest passes left. ${guestsNotAdded(count)}`;

export const guestsNeedCreditMessage = (count: number): string =>
  `Guest passes work only when your pack or membership pays. ${guestsNotAdded(count)}`;

export const guestCardTitle = (index: number, count: number): string =>
  count === 1 ? 'Guest details' : `Guest ${index + 1}`;

export type GuestFormValue = {
  first_name: string;
  last_name: string;
  email: string;
  country_code: string;
  phone: string;
  terms_key: string | null;
};

export type GuestFormValues = { guests: GuestFormValue[] };

export const emptyGuest = (countryCode: string): GuestFormValue => ({
  first_name: '',
  last_name: '',
  email: '',
  country_code: countryCode,
  phone: '',
  terms_key: null,
});

const GUEST_TERMS_REQUIRED_MESSAGE =
  'Please confirm your guest agrees to the terms.';

const GUESTS_TERMS_REQUIRED_MESSAGE =
  'Please confirm your guests agree to the terms.';

const GUESTS_TERMS_CHANGED_MESSAGE =
  'The terms have changed. Tick the box again for your guests.';

// One box can answer for every guest, so its message speaks of them all.
export const guestTermsErrorMessage = (
  message: string | undefined,
  guests: number
): string | undefined => {
  if (guests < 2) return message;
  if (message === GUEST_TERMS_REQUIRED_MESSAGE)
    return GUESTS_TERMS_REQUIRED_MESSAGE;
  if (message === GUEST_TERMS_CHANGED_MESSAGE)
    return GUESTS_TERMS_CHANGED_MESSAGE;
  return message;
};

const GUEST_FIELD_COPY = {
  GUEST_IS_HOST: 'Your guest needs their own email address.',
  GUEST_PHONE_IS_HOST: 'Your guest needs their own phone number.',
  GUEST_PHONE_DUPLICATE: 'Each guest needs their own phone number.',
  GUEST_DUPLICATE: 'Each guest needs a different email address.',
  GUEST_NOT_NEW:
    'Guest passes are only for people new to the studio. This guest has been here before.',
  GUEST_ALREADY_BOOKED: 'This guest is already booked into this class.',
} as const satisfies Record<(typeof GUEST_FIELD_REFUSAL_CODES)[number], string>;

// Copies the backend's PLAIN_TEXT (schemas/common.ts).
const PLAIN_TEXT = /^[^\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]*$/;

const guestName = (emptyMessage: string) =>
  z
    .string()
    .trim()
    .min(1, emptyMessage)
    .max(100, 'Use 100 characters or fewer')
    .regex(PLAIN_TEXT, 'The name must be plain text');

const isValidGuestPhone = (phone: string, countryCode: string) => {
  if (!/^\d{4,15}$/.test(phone.replace(/\D/g, ''))) return false;
  return !!parsePhoneNumberFromString(
    phone,
    countryCode as CountryCode
  )?.isValid();
};

// Hand-copy of the backend's guestInputSchema (schemas/order.ts), whose 422
// pages ops: the rules and their sentences change in both together.
export const buildGuestDetailsSchema = ({
  hostEmail,
  termsKey,
}: {
  hostEmail?: string | null;
  termsKey: string | null;
}) =>
  z
    .object({
      guests: z
        .array(
          z
            .object({
              first_name: guestName('Enter their first name'),
              last_name: guestName('Enter their last name'),
              email: z
                .string()
                .trim()
                .toLowerCase()
                .min(1, 'Enter their email address')
                .email('Enter a valid email address')
                .max(254, 'Use 254 characters or fewer'),
              country_code: z.string().min(1, 'Please select a country'),
              phone: z.string().min(1, 'Enter their phone number'),
              terms_key: z.string().nullable(),
            })
            .superRefine((guest, ctx) => {
              if (
                guest.phone &&
                !isValidGuestPhone(guest.phone, guest.country_code)
              ) {
                ctx.addIssue({
                  code: 'custom',
                  path: ['phone'],
                  message: 'Enter a valid phone number',
                });
              }
              if (termsKey !== null && guest.terms_key !== termsKey) {
                ctx.addIssue({
                  code: 'custom',
                  path: ['terms_key'],
                  message: GUEST_TERMS_REQUIRED_MESSAGE,
                });
              }
            })
        )
        .max(MAX_GUESTS_PER_BOOKING),
    })
    .superRefine(({ guests }, ctx) => {
      const host = hostEmail?.trim().toLowerCase();
      const seen = new Set<string>();
      guests.forEach(({ email }, index) => {
        if (!email) return;
        const message =
          email === host
            ? GUEST_FIELD_COPY.GUEST_IS_HOST
            : seen.has(email)
              ? GUEST_FIELD_COPY.GUEST_DUPLICATE
              : null;
        seen.add(email);
        if (message) {
          ctx.addIssue({
            code: 'custom',
            path: ['guests', index, 'email'],
            message,
          });
        }
      });
    });

export const toGuestPayload = (
  guests: GuestFormValue[],
  termsDocs: ResolvedTerms[]
): GuestInput[] =>
  guests.map((guest) => ({
    first_name: guest.first_name.trim(),
    last_name: guest.last_name.trim(),
    email: guest.email.trim().toLowerCase(),
    phone: guest.phone.replace(/\D/g, ''),
    country_code: toDialCode(guest.country_code),
    ...(termsDocs.length ? { accepted_terms: toAcceptedTerms(termsDocs) } : {}),
  }));

export type GuestRefusal =
  | {
      kind: 'field';
      index: number;
      field: 'email' | 'phone' | 'terms_key';
      message: string;
      // Marked too, without the sentence repeated under it.
      alsoField?: 'phone';
    }
  | { kind: 'list'; message: string; recheck: boolean }
  | { kind: 'toast'; message: string; recheck: true };

const readCount = (details: unknown, key: string): number | null => {
  const value = (details as Record<string, unknown> | null)?.[key];
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : null;
};

const passesLeftCopy = (left: number, canRemoveGuests: boolean) => {
  if (left === 0) return 'You have no guest passes left.';
  return canRemoveGuests
    ? `You have ${passesText(left)} left. Remove a guest to continue.`
    : `You have ${passesText(left)} left, not enough for this class.`;
};

const spotsLeftCopy = (spots: number, canRemoveGuests: boolean) => {
  if (spots === 0) return 'This class has no room for guests.';
  const room = `This class only has room for ${guestsText(spots)}.`;
  return canRemoveGuests ? `${room} Remove a guest to continue.` : room;
};

const GUEST_TERMS_CHANGED_MESSAGE =
  'The terms have changed. Tick the box again for your guest.';

// Classified by code, with the server's sentence only as the fallback. A terms
// refusal without guest_index is the host's own and is left to the terms hook.
// A guest the host can't remove is never what the refusal asks for.
export const guestRefusal = (
  code: string | undefined,
  details: unknown,
  fallback: string,
  { canRemoveGuests = true }: { canRemoveGuests?: boolean } = {}
): GuestRefusal | null => {
  const index = readCount(details, 'guest_index');
  switch (code) {
    case 'GUEST_IS_HOST':
    case 'GUEST_PHONE_IS_HOST':
    case 'GUEST_PHONE_DUPLICATE':
    case 'GUEST_DUPLICATE':
    case 'GUEST_NOT_NEW':
    case 'GUEST_ALREADY_BOOKED':
      return index === null
        ? { kind: 'list', message: GUEST_FIELD_COPY[code], recheck: false }
        : {
            kind: 'field',
            index,
            field:
              code === 'GUEST_PHONE_IS_HOST' || code === 'GUEST_PHONE_DUPLICATE'
                ? 'phone'
                : 'email',
            message: GUEST_FIELD_COPY[code],
            // The backend finds a returning person by email or by phone.
            ...(code === 'GUEST_NOT_NEW' && { alsoField: 'phone' as const }),
          };
    case 'TERMS_ACCEPTANCE_REQUIRED':
    case 'TERMS_ACCEPTANCE_STALE':
      return index === null
        ? null
        : {
            kind: 'field',
            index,
            field: 'terms_key',
            message:
              code === 'TERMS_ACCEPTANCE_STALE'
                ? GUEST_TERMS_CHANGED_MESSAGE
                : GUEST_TERMS_REQUIRED_MESSAGE,
          };
    case 'GUEST_PASS_NONE_LEFT': {
      const left = readCount(details, 'passes_left');
      return {
        kind: 'list',
        message:
          left === null ? fallback : passesLeftCopy(left, canRemoveGuests),
        recheck: true,
      };
    }
    case 'GUEST_NO_SPOTS': {
      const spots = readCount(details, 'spots_left');
      return {
        kind: 'list',
        message:
          spots === null ? fallback : spotsLeftCopy(spots, canRemoveGuests),
        recheck: false,
      };
    }
    case 'GUEST_PASS_NOT_AVAILABLE':
      return {
        kind: 'toast',
        message: "Guest passes can't be used for this booking.",
        recheck: true,
      };
    default:
      return null;
  }
};

type OccurrenceRef = {
  slot_id: string | null;
  override_slot_id: string | null;
  start_time: string;
};

type ClassTimeRef = { items: OccurrenceRef[] };

const sameClassTime = (a: OccurrenceRef, b: OccurrenceRef): boolean =>
  getOccurrenceId(a) === getOccurrenceId(b) &&
  instantOf(a.start_time) === instantOf(b.start_time);

// Every item of a class time shares its start instant, but after a class move
// they can span the old slot and the new one, so any of them counts.
export const guestsAtClassTime = (
  guests: ReceiptGuest[] | null | undefined,
  classTime: ClassTimeRef
): ReceiptGuest[] =>
  (guests ?? []).filter(
    (guest) =>
      isLiveItem(guest) &&
      classTime.items.some((item) => sameClassTime(guest, item))
  );

// An admin can move a guest to a time where the host holds no spot, live or
// cancelled, so no class time of the receipt would list them.
export const guestsApartFromHost = (
  guests: ReceiptGuest[] | null | undefined,
  items: OccurrenceRef[]
): ReceiptGuest[] =>
  (guests ?? []).filter(
    (guest) =>
      isLiveItem(guest) && !items.some((item) => sameClassTime(item, guest))
  );

export const apartGuestsHeading = (count: number): string =>
  count === 1 ? 'Your guest at another time' : 'Your guests at other times';

export const guestAddFor = (
  add: GuestPassAdd | null | undefined,
  classTime: ClassTimeRef
): GuestPassAdd['class_times'][number] | null =>
  add?.class_times.find(
    (entry) =>
      entry.max_guests >= 1 &&
      classTime.items.some((item) => sameClassTime(entry, item))
  ) ?? null;

// Hand-copy of the backend's min spots group (assertMinSpotsAfterPartialAction
// over the host's spots plus the guests who stay): what a customer
// leaves behind is 0 or at least the minimum.
const groupAllowed = (minSpots: number, remaining: number): boolean =>
  minSpots <= 1 || remaining <= 0 || remaining >= minSpots;

export const canRemoveGuest = ({
  minSpots,
  hostSpots,
  activeGuests,
}: {
  minSpots: number;
  hostSpots: number;
  activeGuests: number;
}): boolean => groupAllowed(minSpots, hostSpots + activeGuests - 1);

// With no host spot at its class time, a guest's group is the guests there.
export const canRemoveApartGuest = ({
  minSpots,
  guest,
  apartGuests,
}: {
  minSpots: number;
  guest: ReceiptGuest;
  apartGuests: ReceiptGuest[];
}): boolean =>
  canRemoveGuest({
    minSpots,
    hostSpots: 0,
    activeGuests: apartGuests.filter((other) => sameClassTime(other, guest))
      .length,
  });

// The guests can't stay when the host's spots left plus them fall short. On a
// class with a minimum, the host's last spot takes them too, even when they
// alone would meet it.
export const guestsMustCancel = ({
  minSpots,
  hostSpotsLeft,
  activeGuests,
}: {
  minSpots: number;
  hostSpotsLeft: number;
  activeGuests: number;
}): boolean =>
  activeGuests > 0 &&
  ((minSpots > 1 && hostSpotsLeft === 0) ||
    !groupAllowed(minSpots, hostSpotsLeft + activeGuests));

export const cancelGuestsLabel = (count: number): string =>
  count === 1
    ? 'Also cancel for your guest'
    : `Also cancel for your ${count} guests`;

// The class minimum, when it locks the box, and a pass return the modal's
// description already names are said there once, not again here.
export const cancelGuestsSubtext = ({
  names,
  checked,
  passReturns,
}: {
  names: string[];
  checked: boolean;
  passReturns: boolean;
}): string => {
  const one = names.length === 1;
  if (!checked) {
    return `${joinWithAnd(names)} ${one ? 'stays' : 'stay'} booked. Your guest ${one ? 'pass stays' : 'passes stay'} used.`;
  }
  const sentence = one
    ? 'Your guest is canceled too.'
    : 'Your guests are canceled too.';
  const pass = one
    ? 'Your guest pass comes back.'
    : 'Your guest passes come back.';
  return passReturns ? `${sentence} ${pass}` : sentence;
};

// Guests going with the host count as spots; unticked, the title is the host's
// own spots alone.
export const cancelModalTitle = ({
  spotsCancelled,
  spotsHeld,
  isPartial,
}: {
  spotsCancelled: number;
  spotsHeld: number;
  isPartial: boolean;
}): string => {
  if (isPartial) return `Cancel ${spotsCancelled} of ${spotsHeld} spots?`;
  return spotsCancelled > 1
    ? `Cancel all ${spotsCancelled} spots?`
    : 'Cancel this class?';
};

export const creditsAndPassesReturnLine = (
  credits: number,
  passes: number,
  plan: GuestPassPlanKind
): string =>
  `${credits} ${pluralizeCredits(credits)} and ${passesText(passes)} come back to your ${plan}.`;

// passesReturned is absent from a backend before guest passes.
export const cancelledGuestsSentence = (
  count: number,
  passesReturned?: number
): string | null => {
  if (count === 0) return null;
  const guests =
    count === 1
      ? 'Your guest was canceled too.'
      : `Your ${count} guests were canceled too.`;
  if (passesReturned === undefined) return guests;
  return `${guests} ${passesReturned === 0 ? 'No guest pass returned.' : `${passesText(passesReturned)} returned.`}`;
};

export const removeGuestBody = ({
  firstName,
  hostBooked,
  passReturns,
}: {
  firstName: string;
  hostBooked: boolean;
  passReturns: boolean;
}): string =>
  [
    hostBooked ? 'Your own spot stays booked.' : null,
    `${firstName} gets a cancellation email${passReturns ? ' and your guest pass comes back' : ''}.`,
  ]
    .filter(Boolean)
    .join(' ');

export const guestsAddedToast = (
  guests: Pick<ReceiptGuest, 'first_name'>[]
): { title: string; description: string } =>
  guests.length <= 1
    ? {
        title: 'Guest added',
        description: `We've emailed ${guests[0]?.first_name ?? 'your guest'} their booking.`,
      }
    : {
        title: `${guests.length} guests added`,
        description: "We've emailed each guest their booking.",
      };

export const addGuestsLabel = (count: number): string =>
  count === 1 ? 'Add guest' : `Add ${count} guests`;

type GuestOfStatus = 'upcoming' | 'completed' | 'cancelled';

export const guestOfCopy = (
  host: GuestOf,
  status: GuestOfStatus
): { title: string; note: string | null } => {
  const name = host.first_name;
  if (status === 'cancelled') return { title: `Guest of ${name}`, note: null };
  if (status === 'completed') {
    return { title: `You came as a guest of ${name}`, note: null };
  }
  return {
    title: `You're a guest of ${name}`,
    note: `Only ${name} can cancel or reschedule this booking.`,
  };
};

export const rescheduleGuestNote = (
  guests: number,
  movingAll: boolean
): string | null => {
  if (guests < 1) return null;
  if (!movingAll) {
    return guests === 1
      ? 'Your guest stays booked at the current time.'
      : 'Your guests stay booked at the current time.';
  }
  return guests === 1
    ? 'Your guest moves with you.'
    : `Your ${guests} guests move with you.`;
};

export const noRoomWithGuests = (guests: number): string =>
  `This time doesn't have room for you and your ${guests === 1 ? 'guest' : 'guests'}.`;

export const guestAlreadyBookedMessage = (movingGuests: number): string =>
  `${movingGuests === 1 ? 'Your guest is' : 'One of your guests is'} already booked at that time. Pick another time.`;

// The backend seats a moved host and the guests moving with them together,
// and answers SLOT_FULL when they don't all fit.
export const hasRoomWithGuests = (
  availableSpots: number | null | undefined,
  spots: number,
  guests: number
): boolean =>
  guests < 1 || availableSpots == null || availableSpots >= spots + guests;

// The reschedule link carries how many guests move with the host: a count, so
// nothing about them reaches a URL.
export const readGuestCountParam = (value: string | null): number => {
  const count = Number(value);
  return Number.isInteger(count) && count > 0
    ? Math.min(count, MAX_GUESTS_PER_BOOKING)
    : 0;
};
